import { Link } from 'react-router-dom';
import { PARENT_POINTS } from '@/content/site-content';
import { buttonClasses } from '@/components/ui/button-styles';
import { ArrowRightIcon, BadgeCheckIcon, CheckIcon } from '@/components/ui/Icons';

/**
 * "For Parents", with a mock-up of an in-app conversation beside it.
 *
 * The mock-up is markup rather than a screenshot so the copy inside it stays
 * translatable and it re-colours with the tokens like everything else.
 */
function ConversationMock() {
  return (
    // The floating cards hang off the edges, so the panel is inset to leave
    // them room rather than letting them cover the conversation.
    <div aria-hidden="true" className="relative mx-auto w-full max-w-md pr-10 pb-12 lg:max-w-none">
      <div className="rounded-[1.5rem] bg-white p-4 shadow-lift ring-1 ring-line">
        <p className="ml-auto w-fit max-w-[62%] rounded-xl rounded-br-sm bg-safe-500 px-3 py-2 text-[0.7rem] leading-snug font-semibold text-white">
          Will we have daily calls for every project?
        </p>

        <div className="mt-3 flex items-start gap-2">
          <span className="size-6 shrink-0 rounded-full bg-accent-200" />
          <p className="rounded-xl rounded-bl-sm bg-accent-50 px-3 py-2 text-[0.7rem] leading-snug text-ink">
            Hello! My name is Alex, how can I help you?
          </p>
        </div>

        <p className="mt-3 ml-auto w-fit max-w-[50%] rounded-xl rounded-br-sm bg-brand-600 px-3 py-2 text-[0.7rem] leading-snug text-white">
          Hi! Julia here, I have a few questions I would love to ask you, if that is ok.
        </p>

        <div className="mt-3 flex items-start gap-2">
          <span className="size-6 shrink-0 rounded-full bg-accent-200" />
          <p className="rounded-xl rounded-bl-sm bg-accent-50 px-3 py-2 text-[0.7rem] leading-snug text-ink">
            Nice to meet you, Julia!
          </p>
        </div>

        <p className="mt-3 ml-8 w-fit rounded-full bg-accent-100 px-3 py-1.5 text-[0.7rem] tracking-widest text-ink-soft">
          •••
        </p>

        <div className="mt-4 flex items-center gap-2 rounded-xl border border-line px-3 py-2">
          <span className="flex-1 text-[0.7rem] text-ink-soft">Write your reply…</span>
          <span className="grid size-6 place-items-center rounded-lg bg-brand-600 text-white">
            <ArrowRightIcon className="size-3.5" />
          </span>
        </div>
      </div>

      {/* The job card floating off the bottom-left corner. */}
      <div className="absolute bottom-0 -left-2 w-52 rounded-2xl bg-white p-3 shadow-lift ring-1 ring-line sm:-left-6">
        <div className="flex items-center gap-2">
          <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-sun-100 text-xs">
            📖
          </span>
          <p className="min-w-0">
            <span className="block text-[0.55rem] text-ink-soft">New opportunity</span>
            <span className="block truncate text-[0.7rem] font-bold text-ink">
              Tutor - Math (Remote)
            </span>
          </p>
        </div>
        <p className="mt-2 inline-flex items-center gap-1 rounded-md bg-safe-50 px-1.5 py-0.5 text-[0.55rem] font-bold text-safe-700">
          <CheckIcon className="size-2.5" strokeWidth={3} />
          Verified
        </p>
        <p className="mt-2 flex items-center justify-between text-[0.6rem] font-semibold text-ink-soft">
          <span>$15/hr</span>
          <span>2-4 hrs/day</span>
        </p>
      </div>

      {/* The contact card floating off the right edge. */}
      <div className="absolute top-1/3 right-0 hidden w-28 rounded-2xl bg-white p-3 text-center shadow-lift ring-1 ring-line sm:block">
        <span className="mx-auto grid size-9 place-items-center rounded-full bg-accent-100">
          <BadgeCheckIcon className="size-4 text-accent-500" />
        </span>
        <p className="mt-1.5 text-[0.65rem] font-bold text-ink">Alex Smith</p>
        <div className="mt-2 flex justify-center gap-2">
          <span className="size-6 rounded-full bg-safe-500" />
          <span className="size-6 rounded-full bg-red-400" />
        </div>
      </div>
    </div>
  );
}

export function ParentsSection() {
  return (
    <section id="for-parents" className="container-page scroll-mt-28 py-16 lg:py-20">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <div>
          <h2 className="text-3xl font-extrabold sm:text-4xl">For Parents</h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-ink-soft">
            You stay in the loop without micromanaging. Every opportunity your teen applies to
            routes through you first - you see who&rsquo;s posting, what the job pays, and where it
            is, before anything is confirmed.
          </p>

          <ul className="mt-7 space-y-3">
            {PARENT_POINTS.map((point) => (
              <li key={point} className="flex items-start gap-3 text-sm text-ink">
                <span className="mt-px grid size-5 shrink-0 place-items-center rounded-full bg-brand-600 text-white">
                  <CheckIcon className="size-3" strokeWidth={3} />
                </span>
                {point}
              </li>
            ))}
          </ul>

          <Link to="/register" className={buttonClasses('primary', 'md', 'mt-8')}>
            Learn more for parents
            <ArrowRightIcon className="size-4" />
          </Link>
        </div>

        <ConversationMock />
      </div>
    </section>
  );
}
